import { FC, useState, useCallback, useEffect, useMemo, Fragment } from 'react'
import { IconButton, Typography } from '@material-tailwind/react'
import { PiPlusBold, PiArrowLineDownDuotone } from 'react-icons/pi'
import { useDropzone, FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { formatBytes, mimeTypeExtension } from '../utils/helpers/mime.helpers'
import { useGeneralToast } from '../hooks/toast'
import AgamiAudioFilePreview from './AgamiAudioFilePreview'
import AgamiVideoFilePreview from './AgamiVideoFilePreview'

interface MediaFileDropzoneProps {
  mediaType: number
  editMode: boolean
  prevFileUrl?: string
  prevFileLabel?: string
  mediaFile?: File | null
  setMediaFile: (file: File | null) => void
  uploadSizeLimit: number
  allowedFileTypes: string[]
}

const MediaFileDropzone: FC<MediaFileDropzoneProps> = ({
  mediaType,
  editMode,
  prevFileUrl,
  prevFileLabel,
  mediaFile,
  setMediaFile,
  uploadSizeLimit,
  allowedFileTypes,
}) => {
  const { t } = useTranslation()
  const { error: toastError } = useGeneralToast()

  const [droppedMediaFileUrl, setDroppedMediaFileUrl] = useState<string | null>(null)
  const formattedUploadLimitInMB = formatBytes(uploadSizeLimit)
  const allowedFileExtension = allowedFileTypes?.map((mime) => mimeTypeExtension(mime))
  const accept = allowedFileTypes?.reduce(
    (acc, curr) => ({ ...acc, [curr]: [`.${mimeTypeExtension(curr)}`] }),
    {}
  )

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const errorMessage = fileRejections?.[0]?.errors?.[0]?.message
      if (errorMessage) {
        toastError(errorMessage)
        return
      }

      if (!acceptedFiles || acceptedFiles.length === 0) {
        toastError(t('mediaFileDropzone.somethingWentWrong'))
        return
      }

      setMediaFile(acceptedFiles[0] || null)
    },
    [setMediaFile, t, toastError]
  )

  useEffect(() => {
    let mounted = true

    if (!mediaFile) {
      setDroppedMediaFileUrl(null)
      return
    }

    // DO NOT DECODE VIDEO (too heavy)
    if (mediaType === 2) {
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (!mounted) return
      if (!reader.result) {
        toastError(t('mediaFileDropzone.issueLoadingMediaFile'))
      }
      setDroppedMediaFileUrl(reader.result as string)
    }
    reader.readAsDataURL(mediaFile)

    return () => {
      mounted = false
    }
  }, [mediaFile, mediaType, t, toastError])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
    maxSize: uploadSizeLimit,
  })

  const dropZoneStyle = useMemo(() => {
    if (isDragActive) return 'bg-primary-50 border-primary'
    if (!isDragActive && !droppedMediaFileUrl)
      return 'bg-base-gray border-base-gray-600 hover:bg-base-gray-600 dark:bg-primary dark:hover:bg-primary-600 cursor-pointer'
    if (!isDragActive && droppedMediaFileUrl)
      return 'bg-primary-200 border-primary-400 hover:bg-primary-400 cursor-pointer'
  }, [isDragActive, droppedMediaFileUrl])

  const uploadLabel = useMemo(() => {
    if (mediaType === 1) return t('mediaFileDropzone.uploadAudioFile')
    if (mediaType === 2) return t('mediaFileDropzone.uploadVideoFile')
    return t('mediaFileDropzone.uploadFile')
  }, [mediaType, t])

  return (
    <>
      {mediaType === 1 && (editMode || droppedMediaFileUrl) && (
        <AgamiAudioFilePreview
          audioLabel={mediaFile?.name ?? prevFileLabel}
          audioUrl={droppedMediaFileUrl ?? prevFileUrl}
          openFileDialog={open}
        />
      )}

      {mediaType === 2 && (editMode || droppedMediaFileUrl || mediaFile) && (
        <AgamiVideoFilePreview
          videoLabel={mediaFile?.name ?? prevFileLabel}
          videoUrl={droppedMediaFileUrl ?? prevFileUrl}
          openFileDialog={open}
          showMedia={!mediaFile}
        />
      )}

      <div
        {...getRootProps()}
        data-testid="media-file-dropzone"
        className={`w-full rounded-lg p-10 border border-dashed flex justify-center items-center ${dropZoneStyle}`}
        style={{
          display: editMode || droppedMediaFileUrl || mediaFile ? 'none' : 'flex',
        }}
      >
        <input {...getInputProps()} />
        <div className="flex justify-center items-center gap-4">
          {isDragActive && (
            <>
              <PiArrowLineDownDuotone className="h-6 w-6 dark:text-primary" />
              <Typography className="text-primary dark:text-primary font-bold">
                {t('mediaFileDropzone.dropItLikeHot')}
              </Typography>
            </>
          )}

          {!isDragActive && !droppedMediaFileUrl && (
            <>
              <IconButton
                className="rounded-full bg-primary dark:bg-primary-400 text-accent"
                size="sm"
                color="gray"
              >
                <PiPlusBold className="h-4 w-4" />
              </IconButton>
              <Typography className="text-primary font-bold">{uploadLabel}</Typography>
            </>
          )}
        </div>
      </div>
      <Typography variant="small">
        {t('mediaFileDropzone.fileSizeLimit')}: <em className="font-bold">{formattedUploadLimitInMB}</em>
        {allowedFileTypes && (
          <>
            {' / '}
            {t('mediaFileDropzone.allowedFileTypes')}:{' '}
            <em className="font-bold">
              {allowedFileExtension?.map((filetype) => (
                <Fragment key={filetype}>
                  <span>{filetype}</span>{' '}
                </Fragment>
              ))}
            </em>
          </>
        )}
      </Typography>
    </>
  )
}

export default MediaFileDropzone
